import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ChevronDown, ChevronRight, Upload, FolderPlus, MoreHorizontal, Pencil, Trash2,
  Folder, FolderOpen,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { DocumentFolder } from "@/hooks/use-document-folders";
import { useFolderTreeContext } from "./FolderTreeContext";
import { DocRow } from "./DocRow";
import { NewFolderInput } from "./NewFolderInput";

/** Check if `targetId` is a descendant of `ancestorId` using the pre-computed children map */
function isDescendantOf(
  ancestorId: number,
  targetId: number,
  childrenMap: Map<number | null, DocumentFolder[]>,
): boolean {
  const stack = childrenMap.get(ancestorId) ?? [];
  for (const child of stack) {
    if (child.id === targetId) return true;
    if (isDescendantOf(child.id, targetId, childrenMap)) return true;
  }
  return false;
}

interface FolderNodeProps {
  folder: DocumentFolder;
  depth: number;
}

export function FolderNode({ folder, depth }: FolderNodeProps) {
  const ctx = useFolderTreeContext();
  const isExpanded = ctx.expandedIds.has(folder.id);
  const isRenaming = ctx.renamingId === folder.id;
  const children = ctx.childFoldersByParent.get(folder.id) ?? [];
  const docs = ctx.docsByFolder.get(folder.id) ?? [];
  const indent = depth * 16;

  // Determine if this folder is a valid drop target
  const isDraggingSelf = ctx.draggingFolderId === folder.id;
  const isDraggingDescendant = ctx.draggingFolderId !== null && isDescendantOf(ctx.draggingFolderId, folder.id, ctx.childFoldersByParent);
  const isDropTarget = ctx.dropTargetId === folder.id && !isDraggingSelf && !isDraggingDescendant;
  const isDragging = isDraggingSelf;

  return (
    <div className="w-full">
      {/* Folder header row */}
      <div
        className={`flex items-center gap-1 py-0.5 pr-1 rounded-md hover:bg-muted/50 group w-full min-w-0 transition-colors ${isDropTarget ? "ring-1 ring-blue-400 bg-blue-50 dark:bg-blue-950/30" : ""} ${isDragging ? "opacity-40" : ""}`}
        style={{ paddingLeft: `${indent + 4}px` }}
        draggable={ctx.canWrite && !isRenaming}
        onDragStart={ctx.handleFolderDragStart(folder.id)}
        onDragEnd={ctx.handleDragEnd}
        onDragOver={ctx.onDragOver(folder.id)}
        onDragLeave={ctx.onDragLeave}
        onDrop={ctx.handleDrop(folder.id)}
      >
        <button
          className="shrink-0 flex items-center gap-0.5 p-0.5 rounded hover:bg-muted"
          onClick={() => ctx.toggleExpanded(folder.id)}
          aria-expanded={isExpanded}
          aria-label={`${isExpanded ? "Collapse" : "Expand"} ${folder.name}`}
        >
          {isExpanded
            ? <ChevronDown className="h-3 w-3 text-muted-foreground" />
            : <ChevronRight className="h-3 w-3 text-muted-foreground" />
          }
          {isExpanded
            ? <FolderOpen className="h-3.5 w-3.5" style={folder.color ? { color: folder.color } : undefined} />
            : <Folder className="h-3.5 w-3.5" style={folder.color ? { color: folder.color } : undefined} />
          }
        </button>

        {isRenaming ? (
          <Input
            autoFocus
            className="h-5 text-xs flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 px-0"
            value={ctx.renameValue}
            onChange={e => ctx.setRenameValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") e.currentTarget.blur();
              if (e.key === "Escape") { ctx.onRenameCancel(); e.currentTarget.blur(); }
            }}
            onBlur={() => { if (ctx.renamingId === folder.id) ctx.onRenameSubmit(); }}
          />
        ) : (
          <>
            <button
              className="flex-1 min-w-0 overflow-hidden text-left"
              onClick={() => ctx.toggleExpanded(folder.id)}
            >
              <span className="text-xs font-medium block truncate leading-tight">
                {folder.name}
              </span>
            </button>
            {docs.length > 0 && (
              <span className="text-[10px] text-muted-foreground shrink-0 tabular-nums">{docs.length}</span>
            )}
          </>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
              <MoreHorizontal className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="w-36 text-xs">
            <DropdownMenuItem className="text-xs py-1" onClick={() => ctx.canWrite && ctx.onUploadToFolder(folder.id)} disabled={!ctx.canWrite}>
              <Upload className="h-3 w-3 mr-1.5" />Upload Here
            </DropdownMenuItem>
            <DropdownMenuItem className="text-xs py-1" onClick={() => ctx.canWrite && ctx.onStartCreate(folder.id)} disabled={!ctx.canWrite}>
              <FolderPlus className="h-3 w-3 mr-1.5" />New Subfolder
            </DropdownMenuItem>
            <DropdownMenuItem className="text-xs py-1" onClick={() => ctx.canWrite && ctx.onStartRename(folder.id, folder.name)} disabled={!ctx.canWrite}>
              <Pencil className="h-3 w-3 mr-1.5" />Rename
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-xs py-1 text-destructive focus:text-destructive" onClick={() => ctx.canWrite && ctx.onDeleteFolder(folder.id, folder.name)} disabled={!ctx.canWrite}>
              <Trash2 className="h-3 w-3 mr-1.5" />Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Expanded children */}
      {isExpanded && (
        <div className="w-full">
          {/* New subfolder input */}
          {ctx.creatingInParent === folder.id && (
            <NewFolderInput
              value={ctx.newName}
              onChange={ctx.setNewName}
              onSubmit={() => ctx.onCreateSubmit(folder.id)}
              onCancel={ctx.onCreateCancel}
              depth={depth + 1}
              isCreating={ctx.isCreatingFolder}
            />
          )}

          {/* Child folders (recurse) */}
          {children.map(child => (
            <FolderNode key={child.id} folder={child} depth={depth + 1} />
          ))}

          {/* Documents in this folder */}
          {docs.map(doc => (
            <DocRow key={doc.id} doc={doc} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}
